import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Users, Film } from 'lucide-react';
import ErrorPage from './ErrorPage';

const CastCrew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Detect type from URL path
  const type = window.location.pathname.includes('/tv/') ? 'tv' : 'movie';
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('cast');

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    if (id && type) {
      fetchCredits();
    }
  }, [id, type]);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      setError('');

      const endpoint = type === 'tv'
        ? `${API_BASE}/tv/${id}/credits`
        : `${API_BASE}/movies/${id}/credits`;

      const response = await fetch(endpoint);

      if (response.status === 404) {
        setError('This title is no longer available. It may have been removed from our database.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCredits(data);
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError('Failed to load cast and crew information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-videbits-50 via-pink-50 to-videbits-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-videbits-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !credits) {
    const isNotFound = error && error.includes('no longer available');
    return (
      <ErrorPage
        title={isNotFound ? 'Title Not Found' : 'Unable to Load Credits'}
        message={isNotFound
          ? 'This movie or TV show is no longer available in our database. It may have been removed or the link is incorrect.'
          : 'We had trouble loading the cast and crew information. Please try again later.'
        }
        showHomeButton={true}
        showSearchButton={true}
        showBackButton={false}
      />
    );
  }

  // Sort cast by order
  const sortedCast = [...credits.cast].sort((a, b) => a.order - b.order);

  // Get crew departments, prioritizing key departments
  const priorityDepartments = ['Directing', 'Writing', 'Production', 'Sound', 'Camera', 'Editing'];
  const allDepartments = Object.keys(credits.crew_by_department || {}).sort((a, b) => {
    const aPriority = priorityDepartments.indexOf(a);
    const bPriority = priorityDepartments.indexOf(b);
    if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
    if (aPriority !== -1) return -1;
    if (bPriority !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-videbits-50 via-pink-50 to-videbits-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/${type}/${id}`}
            className="inline-flex items-center text-videbits-600 hover:text-videbits-800 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {type === 'tv' ? 'Series' : 'Movie'}
          </Link>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
            {credits.title} - Cast & Crew
          </h1>
        </div>

        {/* Creators Section (TV only) */}
        {type === 'tv' && credits.creators && credits.creators.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Film className="w-6 h-6 mr-2" />
              Creators
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {credits.creators.map((creator, index) => (
                <Link
                  key={`creator-${creator.id}-${index}`}
                  to={`/person/${creator.id}`}
                  className="group block"
                >
                  <div className="aspect-w-2 aspect-h-3 bg-gray-200 rounded-lg overflow-hidden mb-3">
                    {creator.profile_path ? (
                      <img
                        src={creator.profile_path}
                        alt={creator.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-48 bg-gradient-to-br from-videbits-200 to-pink-200 ${creator.profile_path ? 'hidden' : 'flex'} items-center justify-center`}
                    >
                      <User className="w-12 h-12 text-videbits-400" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm group-hover:text-videbits-600 transition-colors">
                    {creator.name}
                  </h3>
                  <p className="text-xs text-gray-500">Creator</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('cast')}
              className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
                activeTab === 'cast'
                  ? 'bg-videbits-600 text-white'
                  : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
              }`}
            >
              <Users className="w-5 h-5 inline-block mr-2" />
              Cast ({credits.total_cast})
            </button>
            <button
              onClick={() => setActiveTab('crew')}
              className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
                activeTab === 'crew'
                  ? 'bg-videbits-600 text-white'
                  : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
              }`}
            >
              <Film className="w-5 h-5 inline-block mr-2" />
              Crew ({credits.total_crew})
            </button>
          </div>

          <div className="p-8">
            {/* Cast Tab */}
            {activeTab === 'cast' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Cast</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                  {sortedCast.map((actor, index) => (
                    <Link
                      key={`cast-${actor.id}-${index}`}
                      to={`/person/${actor.id}`}
                      className="group block"
                    >
                      <div className="aspect-w-2 aspect-h-3 bg-gray-200 rounded-lg overflow-hidden mb-3">
                        {actor.profile_path ? (
                          <img
                            src={actor.profile_path}
                            alt={actor.name}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-full h-48 bg-gradient-to-br from-videbits-200 to-pink-200 ${actor.profile_path ? 'hidden' : 'flex'} items-center justify-center`}
                        >
                          <User className="w-12 h-12 text-videbits-400" />
                        </div>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm group-hover:text-videbits-600 transition-colors">
                        {actor.name}
                      </h3>
                      {actor.character && (
                        <p className="text-xs text-gray-500 line-clamp-2">as {actor.character}</p>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Crew Tab */}
            {activeTab === 'crew' && (
              <div>
                {allDepartments.map((department) => (
                  <div key={department} className="mb-8 last:mb-0">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{department}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                      {credits.crew_by_department[department].map((member, index) => (
                        <Link
                          key={`${member.id}-${index}`}
                          to={`/person/${member.id}`}
                          className="group block"
                        >
                          <div className="aspect-w-2 aspect-h-3 bg-gray-200 rounded-lg overflow-hidden mb-3">
                            {member.profile_path ? (
                              <img
                                src={member.profile_path}
                                alt={member.name}
                                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-full h-48 bg-gradient-to-br from-videbits-200 to-pink-200 ${member.profile_path ? 'hidden' : 'flex'} items-center justify-center`}
                            >
                              <User className="w-12 h-12 text-videbits-400" />
                            </div>
                          </div>
                          <h3 className="font-semibold text-gray-900 text-sm group-hover:text-videbits-600 transition-colors">
                            {member.name}
                          </h3>
                          {member.job && (
                            <p className="text-xs text-gray-500">{member.job}</p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CastCrew;
